import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { IconComponent } from '@examdojo/core/icon';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { provideFaIcons } from '@examdojo/icons';
import { findUniqueValuesByKey } from '@examdojo/util/find-unique-values';
import { faPlus, faSearch, faTimes } from '@fortawesome/pro-light-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowClickedEvent,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { combineLatest, distinctUntilChanged, map, of, tap } from 'rxjs';
import { ButtonComponent } from '@examdojo/core/button';
import { ComponentSize, DEFAULT_COMPONENT_SIZE } from '@examdojo/core/model';
import { AbstractListViewService } from './abstract-list-view.service';
import { ListViewGridComponent } from './list-view-grid/list-view-grid.component';
import { ListViewSearchFieldComponent } from './list-view-search-field/list-view-search-field.component';
import { ListViewDataBase, ListViewGridMessages } from './master-list-view.model';

@UntilDestroy()
@Component({
  selector: 'dojo-master-list-view',
  imports: [IconComponent, ListViewSearchFieldComponent, ListViewGridComponent, MatTooltipModule, ButtonComponent],
  templateUrl: './master-list-view.component.html',
  styleUrls: ['./master-list-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MasterListViewComponent<D extends ListViewDataBase = ListViewDataBase> implements OnInit {
  constructor(private readonly service: AbstractListViewService<D>) {
    this.manageSearchInputState().pipe(untilDestroyed(this)).subscribe();
    provideFaIcons([faSearch, faPlus, faTimes]);
  }

  @Input() canAdd = true;
  @Input() addButtonText = 'Add';
  @Input() addButtonColor?: ThemePalette;
  @Input() showToolbarAddButton = false;
  @Input() showAddButtonIcon = true;
  @Input() agGridClass = 'ag-theme-basic-table';
  @Input() customDescription?: string;
  @Input() agGridAdditionalClass?: string;
  @Input() sizeSearchFiled: ComponentSize = DEFAULT_COMPONENT_SIZE;
  @Input() hideNumberOfEntries = false;
  @Input() showNumberOfSelectedEntries = false;
  /**
   * Don't hide the search field even when there is no data available.
   * Useful when defining additional (e.g. date) filter fields, like in the datasource-table-job list-view.
   */
  @Input() hideToolbarWhenNoData = true;

  // search-field options
  @Input() searchCtrl = new FormControl<string>('', { nonNullable: true });
  @Input() hideSearch = false;
  @Input() searchLabel = '';
  @Input() searchPlaceholder = '';
  @Input() searchFieldClasses = '';

  // grid options
  @Input() messages?: ListViewGridMessages;
  @Input() columnDefs: ColDef[] = [];
  @Input() gridOptions: GridOptions = {};
  /** @deprecated define the `cellRenderer` on the `ColDef` directly */
  @Input() frameworkComponents: GridOptions['components'] = {};

  @Input() hideLoader = false;
  @Input() incrementalLoading = false;

  // events
  @Output() add = new EventEmitter<void>();
  @Output() gridReady = new EventEmitter<GridReadyEvent>();
  @Output() gridDestroy = new EventEmitter<void>();
  @Output() gridRowClicked = new EventEmitter<RowClickedEvent>();
  @Output() gridRowDoubleClicked = new EventEmitter<RowDoubleClickedEvent>();
  @Output() gridSelectionChanged = new EventEmitter<SelectionChangedEvent>();
  @Output() cellClicked = new EventEmitter<CellClickedEvent>();

  private readonly idProperty = this.service.getIdProperty?.() ?? 'id';

  private readonly numberOfEntries$ = this.service.items$.pipe(
    map(
      (data) =>
        findUniqueValuesByKey(data, this.idProperty).filter((item) => isNotNullish(item[this.idProperty])).length,
    ),
  );
  readonly state = connectState({
    data: this.service.items$,
    numberOfEntries: this.numberOfEntries$,
    displayResolver: this.service.hasItems$.pipe(
      map((hasItems) => {
        return this.hideToolbarWhenNoData ? hasItems : of(true);
      }),
    ),
    isLoading: this.service.loading$.pipe(distinctUntilChanged()),
  });

  numberOfSelectedEntries = computed(() => this.gridApi?.getSelectedRows().length ?? 0);
  gridApi: GridApi | null = null;

  ngOnInit() {
    this.service.getAll?.();
  }

  onGridReady(evt: GridReadyEvent) {
    this.gridApi = evt.api!;
    this.gridReady.emit(evt);
  }

  onGridDestroy() {
    this.gridApi = null;
    this.gridDestroy.emit();
  }

  rowClicked(evt: RowClickedEvent) {
    const item: D | null = evt.data;
    this.service.setActiveItem(item);
    this.gridRowClicked.emit(evt);
  }

  rowDoubleClicked(evt: RowDoubleClickedEvent) {
    const item: D | null = evt.data;
    this.service.setActiveItem(item);
    this.gridRowDoubleClicked.emit(evt);
  }

  addItem() {
    this.service.add?.();
    this.add.emit();
  }

  manageSearchInputState() {
    return combineLatest([this.service.loading$.pipe(distinctUntilChanged()), this.service.hasItems$]).pipe(
      tap(([loading, hasItems]) => {
        if (loading || !hasItems) {
          this.searchCtrl.disable();
        } else {
          this.searchCtrl.enable();
        }
      }),
    );
  }
}
